import { useState } from "react";
import { isEmpty, omit } from "lodash";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useArbitrageFilters } from "src/Modules/Arbitrage/context";
import {
  Box,
  Menu,
  MenuItem,
  MenuList,
  Tooltip,
  IconButton,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  FilterCatalogItem,
  FilterValueType,
} from "src/Modules/Arbitrage/types";
import {
  ExcludedExchangesAutocomplete,
  GenericTextField,
} from "./FilterInputs";

const EditFilterComponent = ({
  currentEditFilter,
}: {
  currentEditFilter: FilterCatalogItem;
}) => {
  const componentsByName: Record<FilterCatalogItem["name"], JSX.Element> = {
    excludedExchanges: (
      <ExcludedExchangesAutocomplete filterName="excludedExchanges" />
    ),
  };

  const genericComponentsByFilterType: Record<
    FilterCatalogItem["type"],
    JSX.Element | null
  > = {
    number: (
      <GenericTextField
        currentEditFilter={currentEditFilter}
        filterName={currentEditFilter.name}
      />
    ),
    string: (
      <GenericTextField
        currentEditFilter={currentEditFilter}
        filterName={currentEditFilter.name}
      />
    ),
    boolean: null,
    array: null,
  };

  return (
    componentsByName[currentEditFilter.name] ??
    genericComponentsByFilterType[currentEditFilter.type] ??
    null
  );
};

export const FiltersMenu = () => {
  const theme = useTheme();

  const {
    activeFiltersByName,
    isSyncingRemoteFilters,
    setCurrentEditFilter,
    scopedFilterList,
    filterCatalogByName,
    setActiveFiltersByName,
    currentEditFilter,
    isMenuOpen,
    setIsMenuOpen,
  } = useArbitrageFilters();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsMenuOpen(false);
  };

  const handleSetActiveFilter = (name: string) => {
    const type = filterCatalogByName[name].type;
    if (type === FilterValueType.boolean) {
      setActiveFiltersByName(
        (prev: any) => {
          return activeFiltersByName[name]
            ? omit(prev, name)
            : { ...prev, [name]: true };
        },
        name,
        activeFiltersByName[name] ? null : true
      );

      setCurrentEditFilter(null);
    } else {
      setCurrentEditFilter(filterCatalogByName[name]);
    }
  };

  return (
    <>
      <Tooltip title="Add Filter" arrow>
        <IconButton
          aria-controls={isMenuOpen ? "demo-customized-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ? "true" : undefined}
          onClick={handleClick}
          style={{
            color: isEmpty(activeFiltersByName)
              ? "white"
              : theme.palette.primary.main,
          }}
        >
          <FilterListIcon style={{ fontSize: "1.3rem" }} />
        </IconButton>
      </Tooltip>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box style={{ display: "flex" }}>
          <MenuList dense>
            {scopedFilterList.map(
              ({ displayName, icon: Icon, name }: FilterCatalogItem) => (
                <MenuItem
                  disabled={isSyncingRemoteFilters}
                  key={displayName}
                  style={{ paddingRight: "2rem" }}
                  onClick={() => handleSetActiveFilter(name)}
                >
                  {isSyncingRemoteFilters ? (
                    <CircularProgress
                      size={23}
                      style={{ marginRight: "1rem" }}
                    />
                  ) : (
                    <Icon
                      style={{
                        marginRight: "1rem",
                        fill:
                          activeFiltersByName[name] !== undefined
                            ? theme.palette.primary.main
                            : "white",
                      }}
                    />
                  )}
                  <span
                    style={{
                      fontWeight:
                        activeFiltersByName[name] !== undefined ? 600 : 300,
                    }}
                  >
                    {displayName}
                  </span>
                </MenuItem>
              )
            )}
          </MenuList>
          {!!currentEditFilter && (
            <Box
              style={{ borderLeft: "0.5px solid white" }}
              p="1rem"
              display="flex"
              flexDirection="column"
            >
              <EditFilterComponent currentEditFilter={currentEditFilter} />
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
};
